import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Screenshot } from '@chronos/shared';
import { apiFetch } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { useProjects, useScreenshotGallery, useUsers } from '@/hooks/queries';
import { ActivityBar, Card, ConfirmButton, EmptyState, ErrorNote, Modal, PageHeader, Spinner } from '@/components/ui';
import { addDays, formatDateTime, formatTime, toDateInput } from '@/lib/format';

export function ScreenshotsPage() {
  const { hasRole, settings } = useAuth();
  const qc = useQueryClient();

  const [date, setDate] = useState(() => toDateInput(new Date()));
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [selected, setSelected] = useState<Screenshot | null>(null);

  const users = useUsers({});
  const projects = useProjects({ pageSize: 200 });
  const interval = settings?.screenshotIntervalMinutes ?? 10;

  const { data, isLoading, error } = useScreenshotGallery({
    date,
    userId: userId || undefined,
    projectId: projectId || undefined,
    intervalMinutes: interval,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/screenshots/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setSelected(null);
      void qc.invalidateQueries({ queryKey: ['screenshots'] });
    },
  });

  const total = (data?.buckets ?? []).reduce((s, b) => s + b.items.length, 0);
  const isToday = date === toDateInput(new Date());

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Screenshots"
        description={`Captures grouped into ${interval}-minute intervals. Dates are in UTC.`}
        actions={
          <div className="flex items-center gap-1">
            <button className="btn-secondary" onClick={() => setDate(toDateInput(addDays(new Date(`${date}T12:00:00`), -1)))} aria-label="Previous day">←</button>
            <input type="date" className="input max-w-[10rem]" value={date} onChange={(e) => setDate(e.target.value)} />
            <button className="btn-secondary" onClick={() => setDate(toDateInput(addDays(new Date(`${date}T12:00:00`), 1)))} disabled={isToday} aria-label="Next day">→</button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <select className="input max-w-xs" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Everyone</option>
          {users.data?.items.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <select className="input max-w-xs" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">All projects</option>
          {projects.data?.items.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <span className="self-center text-sm text-slate-500">{total} capture{total === 1 ? '' : 's'}</span>
      </div>

      <ErrorNote error={error ?? remove.error} />

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : total === 0 ? (
        <Card>
          <EmptyState
            title="No screenshots for this day"
            description="Captures appear here once someone runs the desktop app with a timer going."
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {data?.buckets.map((bucket) => (
            <Card key={`${bucket.userId}-${bucket.bucketStart}`}>
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-800">{bucket.userName}</span>
                  <span className="font-mono text-xs text-slate-500">
                    {formatTime(bucket.bucketStart)} – {formatTime(new Date(new Date(bucket.bucketStart).getTime() + interval * 60_000))}
                  </span>
                </div>
                <span className="badge bg-slate-100 text-slate-600">{bucket.items.length} shot{bucket.items.length === 1 ? '' : 's'}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-5">
                {bucket.items.map((shot) => (
                  <button key={shot.id} className="group text-left" onClick={() => setSelected(shot)}>
                    <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                      <img
                        src={shot.thumbnailUrl ?? shot.url}
                        alt={`Screen capture at ${formatDateTime(shot.capturedAt)}`}
                        loading="lazy"
                        className="aspect-video w-full object-cover transition group-hover:opacity-90"
                      />
                    </div>
                    <div className="mt-1.5 space-y-1">
                      <p className="font-mono text-xs text-slate-600">{formatTime(shot.capturedAt)}</p>
                      <ActivityBar level={shot.activityLevel} />
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={!!selected}
        wide
        title={selected ? `${selected.userName} · ${formatDateTime(selected.capturedAt)}` : ''}
        onClose={() => setSelected(null)}
        footer={
          hasRole('ADMIN') && selected ? (
            <ConfirmButton label="Delete screenshot" confirmLabel="Click again to delete permanently" onConfirm={() => remove.mutate(selected.id)} />
          ) : undefined
        }
      >
        {selected && (
          <div className="space-y-3">
            <img src={selected.url} alt="Full screen capture" className="w-full rounded-lg border border-slate-200" />
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
              <div><dt className="text-xs text-slate-500">Project</dt><dd className="font-medium">{selected.projectName ?? '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Display</dt><dd className="font-medium">{selected.displayLabel ?? '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Resolution</dt><dd className="font-medium">{selected.width && selected.height ? `${selected.width}×${selected.height}` : '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Activity</dt><dd><ActivityBar level={selected.activityLevel} /></dd></div>
              <div><dt className="text-xs text-slate-500">Keyboard activity</dt><dd className="font-medium tabular-nums">{selected.keyboardCount}</dd></div>
              <div><dt className="text-xs text-slate-500">Mouse activity</dt><dd className="font-medium tabular-nums">{selected.mouseCount}</dd></div>
            </dl>
            <p className="text-xs text-slate-400">
              Counts are input events (precise mode) or seconds/cursor moves with input (sampled mode) — never what was typed.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
